import { Alert, Button, Group, Modal, Paper, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { NapakaApi, sporocilo } from '../api/napaka';
import { api, podatki } from '../api/odjemalec';

/**
 * Okno »Pošlji po e-pošti« na računu: prejemnik je e-pošta kupca (lahko se spremeni), predogled besedila s podatki za
 * plačilo; PDF računa gre v priponko (strežnik). Odgovor stranke pride na e-pošto podjetja.
 */
export function RacunPoEposti({ racunId, odprto, zapri }: { racunId: number; odprto: boolean; zapri: () => void }) {
  return (
    <Modal opened={odprto} onClose={zapri} title="Pošlji račun po e-pošti" size="lg">
      {odprto && <Vsebina racunId={racunId} zapri={zapri} />}
    </Modal>
  );
}

function Vsebina({ racunId, zapri }: { racunId: number; zapri: () => void }) {
  const predogled = useQuery({
    queryKey: ['racun-eposta', racunId],
    queryFn: ({ signal }) => podatki(api.GET('/api/racuni/{id}/eposta', { signal, params: { path: { id: racunId } } })),
    gcTime: 0,
  });
  const [email, setEmail] = useState<string | null>(null);
  const prejemnik = email ?? predogled.data?.email ?? '';
  const poslji = useMutation({
    mutationFn: () =>
      podatki(
        api.POST('/api/racuni/{id}/eposta', { params: { path: { id: racunId } }, body: { email: prejemnik.trim() } }),
      ),
    onSuccess: (p) => {
      notifications.show({ color: 'green', message: `Račun je poslan na ${p.email}.` });
      zapri();
    },
  });
  const napakaPolja = poslji.error instanceof NapakaApi ? poslji.error.polja.email : undefined;
  return (
    <Stack gap="sm">
      {predogled.isError && (
        <Alert color="red" variant="light" role="alert">
          {sporocilo(predogled.error)}
        </Alert>
      )}
      {predogled.data && (
        <>
          <TextInput
            label="Prejemnik"
            type="email"
            value={prejemnik}
            onChange={(e) => setEmail(e.currentTarget.value)}
            onBlur={() => setEmail(prejemnik.trim())}
            error={napakaPolja}
            withAsterisk
          />
          <Paper withBorder radius="md" p="sm">
            <Text size="sm" fw={700}>
              {predogled.data.zadeva}
            </Text>
            <Text size="sm" mt="xs" style={{ whiteSpace: 'pre-wrap' }}>
              {predogled.data.besedilo}
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              Priponka: račun v PDF
            </Text>
          </Paper>
        </>
      )}
      {poslji.error && !napakaPolja && (
        <Alert color="red" variant="light" role="alert">
          {sporocilo(poslji.error)}
        </Alert>
      )}
      <Group justify="flex-end" gap="xs">
        <Button variant="subtle" onClick={zapri} disabled={poslji.isPending}>
          Prekliči
        </Button>
        <Button onClick={() => poslji.mutate()} loading={poslji.isPending} disabled={!predogled.data}>
          Pošlji
        </Button>
      </Group>
    </Stack>
  );
}
